<template>
  <div>
    <v-select
      ref="selectEl"
      :label="i18n.global.t('app.supplier.info.name')"
      :items="sortedSuppliers"
      returnObject
      v-model="supplierStore.selectedSupplier"
      :disabled="isDisabled"
      validate-on="input lazy"
      :loading="supplierStore.loadingState === 'loading'"
      :error-messages="
        !supplierStore.selectedSupplier &&
        supplierStore.loadingState === 'success'
          ? i18n.global.t('app.supplier.info.select')
          : supplierStore.loadingState === 'failed'
          ? i18n.global.t('app.supplier.loadingData.error')
          : undefined
      "
      @update:focused="
        (val) => {
          isEditing = val;
        }
      "
      @update:modelValue="
        () => {
          // un-focus element (for whatever reason vuetify doesn't do this on its own in this version)
          blurFunction ? blurFunction() : '';
        }
      "
    >
      <template #message="{ message }">
        <p class="!whitespace-nowrap line-clamp-1 overflow-ellipsis">
          {{ message }}
        </p>
      </template>
      <template #selection="{ item }">
        {{ item.raw.Name }} ({{ item.raw.SupplierId }})
      </template>
      <template #item="{ item, props }">
        <v-list-item
          v-bind="props"
          :title="item.raw.Name"
          :subtitle="item.raw.SupplierId || 'test'"
        ></v-list-item>
      </template>
      <template #append-inner="{ blur, focus }">
        <div
          @vue:mounted="
            () => {
              blurFunction = blur;
              focusFunction = focus;
            }
          "
        >
          <v-fade-transition>
            <div v-show="isEditing && supplierStore.suppliers.length > 2">
              <v-tooltip
                :open-delay="400"
                location="top"
                :text="i18n.global.t('app.supplier.info.sort')"
              >
                <template #activator="{ props }">
                  <div v-bind="props">
                    <v-btn
                      variant="tonal"
                      density="comfortable"
                      icon="mdi-sort-numeric-descending-variant"
                      v-if="sortBy === 'id'"
                      @click.stop="
                        () => {
                          sortBy = 'name';
                          selectEl.menu = true;
                        }
                      "
                    ></v-btn>
                    <v-btn
                      variant="tonal"
                      density="comfortable"
                      @click.stop="
                        () => {
                          sortBy = 'id';
                          selectEl.menu = true;
                        }
                      "
                      icon="mdi-sort-alphabetical-ascending"
                      v-if="sortBy === 'name'"
                    ></v-btn>
                  </div>
                </template>
              </v-tooltip>
            </div>
          </v-fade-transition>
        </div>
      </template>

      <!-- TODO Removed CSV Button until API has an endpoint for this -->
      <!-- <template #append-inner="{ blur, focus }">
         <div
          @vue:mounted="
            () => {
              blurFunction = blur;
              focusFunction = focus;
            }
          "
        >
          <v-scale-transition>
            <v-btn
              v-if="supplierStore.selectedSupplier"
              variant="tonal"
              @click="
                async () => {
                  await downloadData();
                  blur();
                }
              "
              :loading="downloadState === 'loading'"
              prependIcon="mdi-download"
            >
              CSV
            </v-btn>
          </v-scale-transition>
          <v-snackbar v-model="showDownloadInfo" :timeout="5000">
            {{
              i18n.global.t("app.supplier.csvData.loadingData.success", {
                supplier: String(supplierStore.selectedSupplier?.Name),
              })
            }}
            <template v-slot:actions>
              <v-btn
                color="primary"
                variant="text"
                @click="showDownloadInfo = false"
              >
                {{ i18n.global.t('global.close') }}
              </v-btn>
            </template>
          </v-snackbar>
        </div>
      </template> -->
      <template #append>
        <v-btn
          @click="supplierStore.getSuppliers()"
          color="primary"
          v-if="
            supplierStore.loadingState === 'failed' ||
            (supplierStore.loadingState === 'success' &&
              supplierStore.suppliers.length === 0)
          "
          icon="mdi-refresh"
        ></v-btn>
      </template>
    </v-select>
  </div>
</template>

<script setup lang="ts">
// modules
import { computed, nextTick, ref, watch } from "vue";
import { i18n } from "@/locales/index";
import { getCookie, setCookie } from "@/helpers/cookies";
// instances
import { useSupplierStore, useSystemStore } from "@/store";

// get instances
const supplierStore = useSupplierStore();
const systemStore = useSystemStore();

// disable input when the system isn't selected yet or the entries aren't done loading
const isDisabled = computed(() => {
  if (!systemStore.selectedSystem) return true;
  if (supplierStore.loadingState == "loading") return true;
  if (supplierStore.loadingState == "unset") return true;
  if (downloadState.value === "loading") return true;
  return false;
});

const blurFunction = ref<() => void | undefined>();
const focusFunction = ref<() => void | undefined>();

const isEditing = ref(false);
const sortBy = ref<"id" | "name">(
  getCookie("sort-suppliers") === "id" ? "id" : "name"
);
const selectEl = ref<null | any>(null);

const sortedSuppliers = computed(() => {
  if (sortBy.value === "id") {
    return [...supplierStore.suppliers].sort((a, b) =>
      a.SupplierId.localeCompare(b.SupplierId)
    );
  }
  return [...supplierStore.suppliers].sort((a, b) =>
    a.Name.localeCompare(b.Name)
  );
});

watch(sortBy, () => {
  setCookie("sort-suppliers", sortBy.value, 120);
});

// focus on suppliers select when list changes and there are more than one entries
watch(
  () => supplierStore.suppliers,
  async (suppliers) => {
    if (suppliers.length > 1) {
      await nextTick();
      await nextTick();
      if (focusFunction.value) {
        focusFunction.value();
      }
      // selectElement.value?.focus();
    }
  }
);

watch(
  () => systemStore.selectedSystem,
  () => {
    supplierStore.setSelectedSupplier(null);
  }
);

// state and logic for supplier csv download
const showDownloadInfo = ref(false);
const downloadState = ref<loadingStateType>("unset");
async function downloadData() {
  downloadState.value = "loading";
  try {
    await supplierStore.handleCsvDownloadButtonClick();
  } catch (error) {
    // no need for error handling
    // error handling is inside store
  }
  downloadState.value = "success";
  showDownloadInfo.value = true;
}
</script>
